import type { DebugBridge } from './DebugBridge';

export interface TerminalCommand {
  input: string;
  output: string;
  timestamp: number;
}

export class InteractiveTerminal {
  readonly el: HTMLDivElement;
  private bridge: DebugBridge;
  private history: TerminalCommand[] = [];
  private historyIndex: number = -1;
  private inputEl: HTMLInputElement;
  private outputEl: HTMLDivElement;

  constructor(bridge: DebugBridge) {
    this.bridge = bridge;

    this.el = document.createElement('div');
    this.el.className = 'interactive-terminal';

    this.outputEl = document.createElement('div');
    this.outputEl.className = 'terminal-output';
    this.el.appendChild(this.outputEl);

    const inputRow = document.createElement('div');
    inputRow.className = 'terminal-input-row';

    const prompt = document.createElement('span');
    prompt.textContent = '>';
    prompt.className = 'terminal-prompt';
    inputRow.appendChild(prompt);

    this.inputEl = document.createElement('input');
    this.inputEl.type = 'text';
    this.inputEl.className = 'terminal-input';
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        void this.executeCommand(this.inputEl.value);
        this.inputEl.value = '';
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.navigateHistory(-1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.navigateHistory(1);
      }
    });
    inputRow.appendChild(this.inputEl);

    this.el.appendChild(inputRow);
  }

  async executeCommand(command: string): Promise<string> {
    const trimmed = command.trim();
    if (!trimmed) return '';

    try {
      const output = await this.bridge.executeTerminal(trimmed);
      this.history.push({
        input: trimmed,
        output,
        timestamp: Date.now(),
      });
      this.historyIndex = this.history.length;
      this.update();
      return output;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      this.history.push({
        input: trimmed,
        output: 'Error: ' + errorMsg,
        timestamp: Date.now(),
      });
      this.historyIndex = this.history.length;
      this.update();
      return errorMsg;
    }
  }

  private navigateHistory(direction: number): void {
    if (this.history.length === 0) return;

    this.historyIndex += direction;
    if (this.historyIndex < 0) {
      this.historyIndex = 0;
    }
    if (this.historyIndex > this.history.length) {
      this.historyIndex = this.history.length;
    }

    if (this.historyIndex < this.history.length) {
      this.inputEl.value = this.history[this.historyIndex].input;
    } else {
      this.inputEl.value = '';
    }
  }

  getHistory(): TerminalCommand[] {
    return [...this.history];
  }

  render(): HTMLElement {
    this.update();
    return this.el;
  }

  update(): void {
    this.outputEl.innerHTML = '';

    for (const cmd of this.history) {
      const entry = document.createElement('div');
      entry.className = 'terminal-entry';

      const inputLine = document.createElement('div');
      inputLine.className = 'terminal-cmd-input';
      inputLine.textContent = '> ' + cmd.input;
      entry.appendChild(inputLine);

      if (cmd.output) {
        const outputLine = document.createElement('div');
        outputLine.className = 'terminal-cmd-output';
        outputLine.textContent = cmd.output;
        entry.appendChild(outputLine);
      }

      this.outputEl.appendChild(entry);
    }

    this.outputEl.scrollTop = this.outputEl.scrollHeight;
  }

  destroy(): void {
    if (this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
  }
}
